import { Controller } from 'react-hook-form';
import { useEffect, useState } from 'react';
import {
  Autocomplete,
  InputText,
  SidePanelRow,
  ToggleInput,
  InputNumber,
} from '@ui/src';
import { DetailsFormTypes } from './types';

import cn from './styles.module.css';

const DetailsForm = ({
  control,
  errors,
  onSubmit,
  countriesOptions,
  children,
  sortCodeValue,
}: DetailsFormTypes): React.JSX.Element => {
  const [sortCode, setSortCode] = useState('');

  useEffect(() => {
    if (sortCodeValue) {
      setSortCode(sortCodeValue);
    }
  }, [sortCodeValue]);

  return (
    <form onSubmit={onSubmit} className={cn.details}>
      <SidePanelRow caption="BIC">
        <Controller
          control={control}
          name="bic"
          render={({ field: { onChange, value, name } }) => (
            <InputText
              placeholder="Enter bic..."
              name={name}
              value={value}
              onChange={onChange}
              error={errors?.bic?.message}
            />
          )}
        />
      </SidePanelRow>

      <SidePanelRow caption="SortCode">
        <Controller
          control={control}
          name="sortcode"
          render={({ field: { onChange, value, name } }) => {
            const onKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
              let inputValue = value;

              if (event.key !== 'Backspace') {
                const regex = /.{1,2}/g;

                inputValue = value
                  ?.replace(/[^0-9]/g, '')
                  ?.match(regex)
                  ?.join('-') as string;
              }

              setSortCode(inputValue);
            };

            return (
              <InputText
                placeholder="Enter Sort Code..."
                name={name}
                value={sortCode}
                onKeyUp={onKeyUp}
                maxLength={8}
                onChange={(e) => {
                  onChange(e);

                  if (!e.target.value) {
                    setSortCode('');
                  }
                }}
                error={errors?.sortcode?.message}
              />
            );
          }}
        />
      </SidePanelRow>

      <SidePanelRow caption="Fin. Year start">
        <Controller
          control={control}
          name="finyearstart"
          render={({ field: { onChange, value, name } }) => (
            <InputText
              placeholder="Enter Fin. Year start..."
              name={name}
              value={value}
              onChange={onChange}
              error={errors?.finyearstart?.message}
            />
          )}
        />
      </SidePanelRow>
      <SidePanelRow caption="VAT Percent">
        <Controller
          control={control}
          name="vatPercent"
          render={({ field: { onChange, value, name } }) => (
            <InputNumber
              placeholder="Enter VAT Percent"
              name={name}
              value={value}
              onChange={(e) => {
                let vatValue = e.target.value;

                if (Number(e.target.value) < 0) {
                  vatValue = '0';
                } else if (Number(e.target.value) > 100) {
                  vatValue = '100';
                }

                onChange(vatValue);
              }}
              error={errors?.vatPercent?.message}
            />
          )}
        />
      </SidePanelRow>
      <SidePanelRow caption="Country">
        <Controller
          control={control}
          name="countryId"
          render={({ field: { onChange, value, name } }) => (
            <Autocomplete
              placeholder="Select country"
              name={name}
              onChange={onChange}
              value={value}
              list={countriesOptions}
              error={errors?.countryId?.message}
            />
          )}
        />
      </SidePanelRow>

      <SidePanelRow caption="Address">
        <Controller
          control={control}
          name="address"
          render={({ field: { onChange, value, name } }) => (
            <InputText
              placeholder="Enter address..."
              name={name}
              value={value}
              onChange={onChange}
              error={errors?.address?.message}
            />
          )}
        />
      </SidePanelRow>

      <SidePanelRow caption="Website">
        <Controller
          control={control}
          name="website"
          render={({ field: { onChange, value, name } }) => (
            <InputText
              placeholder="Enter website..."
              name={name}
              value={value}
              onChange={onChange}
              error={errors?.website?.message}
            />
          )}
        />
      </SidePanelRow>

      <SidePanelRow caption="Email">
        <Controller
          control={control}
          name="email"
          render={({ field: { onChange, value, name } }) => (
            <InputText
              placeholder="Enter email..."
              name={name}
              value={value}
              onChange={onChange}
              error={errors?.email?.message}
            />
          )}
        />
      </SidePanelRow>

      <SidePanelRow caption="Phone">
        <Controller
          control={control}
          name="phone"
          render={({ field: { onChange, value, name } }) => (
            <InputText
              placeholder="Enter phone..."
              name={name}
              value={value}
              onChange={onChange}
              error={errors?.phone?.message}
            />
          )}
        />
      </SidePanelRow>

      <SidePanelRow caption="License number">
        <Controller
          control={control}
          name="license"
          render={({ field: { onChange, value, name } }) => (
            <InputText
              placeholder="Enter License Number..."
              name={name}
              value={value}
              onChange={onChange}
              error={errors?.license?.message}
            />
          )}
        />
      </SidePanelRow>

      <SidePanelRow caption="Regulator">
        <Controller
          control={control}
          name="regulator"
          render={({ field: { onChange, value, name } }) => (
            <InputText
              placeholder="Enter regulator..."
              name={name}
              value={value}
              onChange={onChange}
              error={errors?.regulator?.message}
            />
          )}
        />
      </SidePanelRow>
      <SidePanelRow caption="Legal Name">
        <Controller
          control={control}
          name="legalName"
          render={({ field: { onChange, value, name } }) => (
            <InputText
              placeholder="Enter legal name..."
              name={name}
              value={value}
              onChange={onChange}
              error={errors?.legalName?.message}
            />
          )}
        />
      </SidePanelRow>
      <SidePanelRow>
        <Controller
          control={control}
          name="customersDemoModeAvailable"
          render={({ field: { onChange, value, name } }) => (
            <ToggleInput
              name={name}
              title="Customers demo mode available"
              checked={value}
              onChange={(e) => onChange(e.target.checked)}
            />
          )}
        />
      </SidePanelRow>
      {children}
    </form>
  );
};

export default DetailsForm;
